import React, { useState, useContext, useMemo } from 'react';
// MUI
import {
  Box,
  Card,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  Pagination,
  Divider,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { numberFormat } from '../../../helpers/helperFunctions';
import { Search, Star, Autorenew, Inventory2Outlined } from '@mui/icons-material';
// CONTEXT
import useProductSamplingv2Context from '../context/useProductSamplingv2Context';
// COMPONENTS
import ActionMenu from './ActionMenu';

const statusArr = [
  { label: 'All Status', value: null },
  { label: 'Draft', value: 1 },
  { label: 'For Confirmation', value: 2 },
  { label: 'Partial Confirmed', value: 3 },
  { label: 'Confirmed', value: 4 },
  { label: 'Finalized', value: 5 },
];

const PAGE_SIZE = 10;

export default function Table() {
  const { filterForm, setFilterForm, overview } = useContext(useProductSamplingv2Context);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [sortModel, setSortModel] = useState([]);

  const handleStatusStyle = (value) => {
    switch (value) {
      case 1:
        return {
          color: 'default',
          sx: {},
        };

      case 2:
        return {
          color: 'warning',
          sx: {},
        };

      case 3:
        return {
          color: undefined,
          sx: {
            backgroundColor: '#FFDE21',
            color: '#000',
          },
        };

      case 4:
      case 5:
        return {
          color: 'success',
          sx: {},
        };

      default:
        return {
          color: 'default',
          sx: {},
        };
    }
  };

  const columns = [
    {
      field: 'plan_id',
      headerName: 'Plan ID',
      sortable: true,
      width: 180,
    },
    {
      field: 'title',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography fontWeight={600}>{params.row.title}</Typography>

          <Typography variant='caption' color='text.secondary'>
            Effectivity Date: {params.row.effectivity_month} {params.row.effectivity_year}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size='small'
          label={params.row.category}
          sx={{
            bgcolor: params.row.category === 'Regular' ? '#2e7d32' : '#EF6C00',
            color: '#fff',
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: 'sub_category',
      headerName: 'Sub-Category',
      sortable: true,
      width: 220,
    },
    {
      field: 'item',
      headerName: 'Items',
      width: 90,
      align: 'center',
      sortable: true,
      headerAlign: 'center',
    },
    {
      field: 'requested_qty',
      headerName: 'Requested Quantity',
      width: 110,
      align: 'center',
      sortable: true,
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography fontWeight={600} color={'text.secondary'}>
          {numberFormat(params.value) ?? '–'}
        </Typography>
      ),
    },
    {
      field: 'confirmed_qty',
      headerName: 'Confirmed Quantity',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      renderCell: (params) => (
        <Typography fontWeight={600} color={params.value ? 'success.main' : 'text.secondary'}>
          {numberFormat(params.value) ?? '–'}
        </Typography>
      ),
    },
    {
      field: 'owner',
      headerName: 'Owner',
      sortable: true,
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <Chip
            size='small'
            label={row.status}
            color={handleStatusStyle(row?.status_id)?.color}
            sx={handleStatusStyle(row?.status_id)?.sx}
          />
        );
      },
    },

    {
      field: 'action',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ActionMenu row={params.row} />,
    },
  ];

  const filteredItems = useMemo(() => {
    let result = overview?.items ?? [];

    if (filterForm?.category_id) {
      result = result.filter((row) => row.category_id === filterForm.category_id);
    }

    if (statusFilter !== null) {
      result = result.filter((row) => row.status_id === statusFilter);
    }

    if (searchText.trim()) {
      const search = searchText.trim().toLowerCase();
      result = result.filter(
        (row) =>
          row.title?.toLowerCase().includes(search) || row.plan_id?.toLowerCase().includes(search),
      );
    }

    return result;
  }, [overview?.items, filterForm?.category_id, statusFilter, searchText]);

  const sortedItems = useMemo(() => {
    if (!sortModel.length) {
      return filteredItems;
    }

    const { field, sort } = sortModel[0];

    const sorted = [...filteredItems].sort((a, b) => {
      let valueA = a[field];
      let valueB = b[field];

      // Status should sort using status_id
      if (field === 'status') {
        valueA = a.status_id;
        valueB = b.status_id;
      }

      if (field === 'category') {
        valueA = a.category ?? '';
        valueB = b.category ?? '';
      }
      // Sub-category
      if (field === 'sub_category') {
        valueA = a.sub_category ?? '';
        valueB = b.sub_category ?? '';
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sort === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      if (valueA < valueB) return sort === 'asc' ? -1 : 1;
      if (valueA > valueB) return sort === 'asc' ? 1 : -1;

      return 0;
    });

    return sorted;
  }, [filteredItems, sortModel]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, page]);

  const pageCount = Math.ceil(filteredItems.length / PAGE_SIZE);

  const allItems = overview?.items ?? [];
  const countAll = allItems.length;
  const countRegular = allItems.filter((row) => row?.category_id === 1).length;
  const countSpecial = allItems.filter((row) => row?.category_id === 2).length;

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleTabChange = (e, v) => {
    setTab(v);
    setPage(1);
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tab
          icon={<Inventory2Outlined />}
          iconPosition='start'
          onClick={() => setFilterForm({ ...filterForm, category_id: null })}
          label={
            <Stack direction='row' spacing={1} alignItems='center'>
              <span>ALL</span>
              <Chip size='small' label={countAll} />
            </Stack>
          }
        />

        <Tab
          icon={<Autorenew />}
          iconPosition='start'
          onClick={() => setFilterForm({ ...filterForm, category_id: 1 })}
          label={
            <Stack direction='row' spacing={1} alignItems='center'>
              <span>REGULAR SAMPLING</span>
              <Chip size='small' label={countRegular} />
            </Stack>
          }
        />

        <Tab
          icon={<Star />}
          iconPosition='start'
          onClick={() => setFilterForm({ ...filterForm, category_id: 2 })}
          label={
            <Stack direction='row' spacing={1} alignItems='center'>
              <span>SPECIAL SAMPLING</span>
              <Chip size='small' label={countSpecial} color='success' />
            </Stack>
          }
        />
      </Tabs>

      <Box p={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#fafafa',
          }}
        >
          <TextField
            size='small'
            value={searchText}
            onChange={handleSearch}
            placeholder='Search by plan name or ID'
            sx={{ width: 280, bgcolor: '#fff', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

          {statusArr?.map((row) => (
            <Chip
              key={row?.label}
              label={row?.label}
              clickable
              onClick={() => handleStatusFilter(row.value)}
              sx={{
                backgroundColor: statusFilter === row?.value ? '#2e7d32' : '#fff',
                color: statusFilter === row?.value ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                border: '1px solid',
                borderColor: statusFilter === row?.value ? '#2e7d32' : 'divider',
                '&:hover': {
                  backgroundColor: statusFilter === row?.value ? '#2e7d32' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          ))}
        </Box>

        <DataGridPro
          rows={paginatedItems}
          columns={columns}
          loading={overview?.loading}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
          rowHeight={68}
          headerHeight={48}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPage(1);
          }}
          sx={{
            border: 0,

            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#fafafa',
              borderBottom: '1px solid #e0e0e0',
            },

            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              borderBottom: '1px solid #f0f0f0',
            },

            '& .MuiDataGrid-row:hover': {
              bgcolor: '#fafafa',
            },
          }}
        />

        {pageCount > 1 && (
          <>
            <Divider sx={{ mx: -2, mt: 2 }} />
            <Box display='flex' justifyContent='flex-end' pt={2}>
              <Pagination
                page={page}
                count={pageCount}
                onChange={(_, value) => setPage(value)}
                color='primary'
                size='small'
              />
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
}
